<script setup lang="ts">
// PairingRequestRow — Syncthing 式待配对行 (v3.13.58)。
//
// 一行 = 一个待配对设备: 识别色块 + 名称/短指纹 + 来源/时间 + 剩余倒计时 +
// 行内 [详情] [拒绝] [接受]。独立 setup 上下文让每行持有自己的 useCountdown;
// v-for 以 fingerprint 为 key, 请求被处理后该行移除、其余行不受影响。
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, Clock, Info, UserPlus, X } from 'lucide-vue-next'
import Button from '@/components/ui/Button.vue'
import { peerHost, type PairingRequest } from '@/api/cluster'
import { useCountdown } from '@/composables/useCountdown'

const { t } = useI18n()

interface Props {
  request: PairingRequest
  accepting: boolean
  rejecting: boolean
  color: string
  displayName: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  accept: []
  reject: []
  details: []
}>()

const requestAt = computed<string | undefined>(() => props.request.at)
const { remainingLabel, urgent, expired } = useCountdown(requestAt, 10 * 60 * 1000)

const shortFp = computed<string>(() => props.request.fingerprint.slice(0, 7))

// v3.19.27: 副行显示"来源 IP"(去掉临时端口) — 操作者认机器靠 IP/域名,
// 而不是 ip:ephemeral-port。完整地址 (含端口) 仍在详情弹窗。
const addrText = computed<string>(() => peerHost(props.request.addr) || '—')

// 请求时间 — 短格式 HH:mm (完整字段在详情弹窗里)
const requestedAtShort = computed<string>(() => {
  if (!props.request.at) return ''
  const d = new Date(props.request.at)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
})

function accept(): void {
  emit('accept')
}
function reject(): void {
  emit('reject')
}
function details(): void {
  emit('details')
}
</script>

<template>
  <div class="flex items-center gap-2 px-3 py-2 transition-colors duration-fast hover:bg-warning/5">
    <!-- 识别色块 (fingerprint 派生色, identicon 简化替代) -->
    <div
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white shadow-sm"
      :style="{ backgroundColor: color }"
      aria-hidden="true"
    >
      <UserPlus :size="15" />
    </div>

    <!-- 名称 + 短指纹 / 来源 + 时间 -->
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-1.5">
        <span class="min-w-0 truncate text-sm font-semibold text-fg" :title="displayName">{{ displayName }}</span>
        <code
          class="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-xs text-fg-subtle"
          :title="request.fingerprint"
        >{{ shortFp }}</code>
      </div>
  <p class="mt-0.5 truncate text-xs text-fg-muted" :title="request.addr">
    <template v-if="addrText !== '—'">{{ t('cluster.pairing.fromHost', { host: addrText }) }}</template>
    <template v-else>—</template>
    <template v-if="requestedAtShort">
      <span class="mx-1.5" aria-hidden="true">·</span>{{ requestedAtShort }}
    </template>
  </p>
    </div>

    <!-- 右侧纵向栈: 倒计时在上, 操作按钮在下 -->
    <div class="flex shrink-0 flex-col items-end gap-1">
      <span
        class="flex items-center gap-1 font-mono text-xs tabular-nums"
        :class="urgent || expired ? 'text-danger' : 'text-warning'"
        :title="t('cluster.pairing.fieldTtl')"
      >
        <Clock v-if="urgent || expired" :size="11" class="animate-pulse" />
        <Clock v-else :size="11" class="opacity-60" />
        {{ remainingLabel }}
      </span>
      <div class="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          icon-only
          :aria-label="t('cluster.pairing.viewDetails')"
          @click="details"
        >
          <Info :size="14" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          :loading="rejecting"
          :disabled="accepting"
          @click="reject"
        >
          <template #icon><X :size="13" /></template>
          {{ t('cluster.pairing.reject') }}
        </Button>
        <Button
          variant="primary"
          size="sm"
          :loading="accepting"
          :disabled="rejecting"
          @click="accept"
        >
          <template #icon><Check :size="13" /></template>
          {{ t('cluster.pairing.accept') }}
        </Button>
      </div>
    </div>
  </div>
</template>
